import React from "react";
import { Link } from "react-router-dom";
import {ArrowLeft, MapPin, Syringe, AlertTriangle} from 'lucide-react';
import { motion } from "framer-motion";
import BookingModal from "../../components/BookingModal";

const VaccinesNeededIndiaTravel: React.FC = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      <section className="relative py-20 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources" className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:gap-3 transition-all mb-8">
            <ArrowLeft className="w-5 h-5" />
            Back to Resources
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold">Destinations</div>
              <span className="text-sm text-gray-500 dark:text-gray-400">10 min read</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Do I Need Vaccines for India? Complete Travel Health Guide
            </h1>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Essential vaccinations and health preparations for traveling to India from British Columbia. Learn about hepatitis A, typhoid, malaria prevention, and more.
            </p>
          </motion.div>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <img src="https://static.lumi.new/5b/5b378f7338ebb6f2002b61a514e4161d.png" alt="India travel health" className="w-full h-96 object-cover rounded-2xl mb-12" />

          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Essential Vaccines for India Travel</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            India is a diverse country with varying health risks depending on where you're traveling. Whether you're visiting bustling cities like Delhi and Mumbai, exploring rural villages, or trekking in the Himalayas, proper vaccination is essential for a safe trip.
          </p>

          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 mb-8">
            <h3 className="font-bold text-red-900 dark:text-red-300 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Routine Vaccines - Update Before Travel
            </h3>
            <p className="text-red-800 dark:text-red-200">
              Ensure your routine vaccines are up-to-date: MMR (measles, mumps, rubella), Tdap (tetanus, diphtheria, pertussis), varicella (chickenpox), and annual influenza.
            </p>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Syringe className="w-6 h-6 text-cyan-500" />
            Recommended Vaccines for All Travelers to India
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Hepatitis A</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Risk:</strong> Contaminated food and water - very common in India</p>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Doses:</strong> 2 doses (0 and 6-12 months apart)</p>
              <p className="text-gray-700 dark:text-gray-300"><strong>Cost:</strong> ~$70-90 per dose</p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Typhoid</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Risk:</strong> Contaminated food/water, particularly in rural areas</p>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Options:</strong> Injectable (1 dose) or oral capsules (4 doses)</p>
              <p className="text-gray-700 dark:text-gray-300"><strong>Cost:</strong> ~$50-70 (injection)</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Vaccines for Specific Situations</h3>

          <div className="space-y-6 mb-8">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Japanese Encephalitis</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Who needs it:</strong> Travelers spending &gt;1 month in rural areas, especially during monsoon season</p>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Doses:</strong> 2 doses (0 and 28 days)</p>
              <p className="text-gray-700 dark:text-gray-300"><strong>Cost:</strong> ~$250-300 per dose</p>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Rabies (Pre-Exposure)</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Who needs it:</strong> Long-term travelers, adventure travelers, those visiting rural areas</p>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Doses:</strong> 3 doses (0, 7, and 21-28 days)</p>
              <p className="text-gray-700 dark:text-gray-300"><strong>Cost:</strong> ~$225-250 per dose</p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Hepatitis B</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Who needs it:</strong> Healthcare workers, long-term travelers, those at risk of medical procedures</p>
              <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Doses:</strong> 3 doses (0, 1, and 6 months)</p>
              <p className="text-gray-700 dark:text-gray-300"><strong>Note:</strong> Some doses may be publicly covered</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <MapPin className="w-6 h-6 text-cyan-500" />
            Malaria Prevention in India
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Malaria risk exists in many parts of India, particularly rural areas below 2,000m elevation. Major cities like Delhi, Mumbai, and popular tourist areas have low to no risk.
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 mb-6">
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">High-Risk Areas for Malaria:</h4>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
              <li>Rural areas in Odisha, Chhattisgarh, Jharkhand</li>
              <li>Northeast states (Assam, Meghalaya, Mizoram, etc.)</li>
              <li>Andaman and Nicobar Islands</li>
              <li>Parts of Rajasthan, Gujarat, and Madhya Pradesh</li>
            </ul>
          </div>

          <p className="text-gray-700 dark:text-gray-300 mb-6">
            <strong>Antimalarial Medications:</strong> Options include Malarone (atovaquone-proguanil), doxycycline, or mefloquine. Your travel health provider will recommend the best option based on your itinerary and medical history.
          </p>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Additional Health Precautions</h3>
          <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li><strong>Traveler's Diarrhea:</strong> Consider Dukoral vaccine and bring treatment medications</li>
            <li><strong>Dengue:</strong> No vaccine available for travelers - focus on mosquito bite prevention</li>
            <li><strong>Water Safety:</strong> Drink only bottled or purified water, avoid ice</li>
            <li><strong>Food Safety:</strong> Eat well-cooked food, avoid street food if possible</li>
            <li><strong>Air Quality:</strong> Consider masks in heavily polluted cities, especially Delhi</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">When to Book Your Consultation</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Book your travel health consultation at least 6-8 weeks before departure to allow time for multi-dose vaccines. If you're traveling sooner, don't worry - partial protection is better than none.
          </p>

          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-lg mb-3 text-gray-900 dark:text-white">Total Estimated Vaccine Cost for India:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex justify-between"><span>Hepatitis A (2 doses)</span><span className="font-bold">$140-180</span></li>
              <li className="flex justify-between"><span>Typhoid (1 dose)</span><span className="font-bold">$50-70</span></li>
              <li className="flex justify-between border-t pt-2"><span className="font-bold">Minimum Total:</span><span className="font-bold text-cyan-600 dark:text-cyan-400">$190-250</span></li>
            </ul>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Additional vaccines depend on your itinerary and length of stay</p>
          </div>
        </div>
      </article>

      <section className="py-16 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Planning a Trip to India?</h2>
          <p className="text-xl text-white/90 mb-8">Get personalized vaccine recommendations based on your specific itinerary and travel style.</p>
          <button onClick={() => setIsBookingModalOpen(true)} className="px-8 py-4 bg-white text-cyan-600 font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
            Book Virtual Consult
          </button>
        </div>
      </section>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </div>
  );
};

export default VaccinesNeededIndiaTravel;